import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavoriteArtists } from '../../hooks/useFavoriteArtists';
import { useFavoriteShops } from '../../hooks/useFavoriteShops';
import { useAuth } from '../../hooks/useAuth';

export type FavoriteArtistType = 'mock' | 'user' | 'shop';

interface FavoriteButtonProps {
  artistId: string;
  artistType: FavoriteArtistType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const PARTICLES = [
  { angle: 0,   delay: 0 },
  { angle: 45,  delay: 30 },
  { angle: 90,  delay: 10 },
  { angle: 135, delay: 40 },
  { angle: 180, delay: 20 },
  { angle: 225, delay: 50 },
  { angle: 270, delay: 15 },
  { angle: 315, delay: 35 },
];

export function FavoriteButton({
  artistId,
  artistType,
  size = 'sm',
  className = '',
}: FavoriteButtonProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isFavorite: isArtistFavorite, toggleFavorite: toggleArtistFavorite } = useFavoriteArtists();
  const { isFavorite: isShopFavorite, toggleFavorite: toggleShopFavorite } = useFavoriteShops();
  const [isAnimating, setIsAnimating] = useState(false);
  const [showParticles, setShowParticles] = useState(false);

  const isFavorite = artistType === 'shop'
    ? isShopFavorite(artistId)
    : isArtistFavorite(artistId, artistType);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setIsAnimating(true);
      const willBeFavorite = artistType === 'shop' ? !isShopFavorite(artistId) : !isArtistFavorite(artistId, artistType);
      if (willBeFavorite) {
        setShowParticles(true);
        setTimeout(() => setShowParticles(false), 700);
      }
      if (artistType === 'shop') {
        await toggleShopFavorite(artistId);
      } else {
        await toggleArtistFavorite(artistId, artistType);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    } finally {
      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  const sizeClasses = size === 'lg' ? 'w-12 h-12' : size === 'md' ? 'w-11 h-11' : 'w-8 h-8';
  const iconSize = size === 'lg' ? 'text-xl' : size === 'md' ? 'text-base' : 'text-sm';
  const particleSize = size === 'lg' ? 5 : size === 'md' ? 4 : 3;
  const particleDist = size === 'lg' ? 22 : size === 'md' ? 18 : 14;

  return (
    <button
      onClick={handleClick}
      className={`${sizeClasses} rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center transition-all duration-200 hover:scale-110 relative overflow-visible ${className}`}
      style={{
        transform: isAnimating ? 'scale(1.35)' : undefined,
        transition: isAnimating
          ? 'transform 0.15s cubic-bezier(0.34,1.56,0.64,1)'
          : 'transform 0.25s ease',
      }}
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
    >
      {/* パーティクル */}
      {showParticles && PARTICLES.map((p, i) => {
        const rad = (p.angle * Math.PI) / 180;
        const tx = Math.cos(rad) * particleDist;
        const ty = Math.sin(rad) * particleDist;
        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              width: particleSize,
              height: particleSize,
              borderRadius: '50%',
              background: i % 2 === 0 ? '#f472b6' : '#fb923c',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: `heartParticle 0.6s ease-out ${p.delay}ms forwards`,
              '--tx': `${tx}px`,
              '--ty': `${ty}px`,
            } as React.CSSProperties}
          />
        );
      })}

      <i
        className={`${
          isFavorite ? 'ri-heart-fill text-pink-500' : 'ri-heart-line text-white'
        } ${iconSize} transition-colors duration-200`}
        style={
          isAnimating && isFavorite
            ? { filter: 'drop-shadow(0 0 4px #f472b6)' }
            : undefined
        }
      />

      <style>{`
        @keyframes heartParticle {
          0%   { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(0); opacity: 0; }
        }
      `}</style>
    </button>
  );
}
